/**
 * Registrar Ventas Generales — migración web de ares9610 ("Cobros GPS").
 *
 * Registra una venta miscelánea (GPS, seguros, data crédito, etc.) en la
 * tabla gps. Reglas del escritorio (con su validación de asociarprestamogps
 * corregida, documentada en el backend):
 *   - Con préstamo asociado o a crédito: queda pendiente (cobrado = 0).
 *   - Contado sin préstamo: se cobra completa y exige el tipo de pago.
 *   - El préstamo es obligatorio para crédito cuando asociarprestamogps='S'.
 * Los administradores pueden gestionar las categorías (alta/baja; "Gps" no
 * se puede borrar). Al guardar se imprime el recibo de ventas misceláneas.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const formatCedula = (v) => {
  const d = String(v || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 10)}-${d.slice(10)}`;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color, extra }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
      {extra}
    </Box>
  );
}

// Recibo de la venta (equivalente web de imprimircobrogps del escritorio).
export function imprimirReciboVentaGeneral(r) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Recibo de Cobros Ventas Miscelaneas</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:420px;margin:0 auto">
      <h3 style="text-align:center;margin:0 0 2px">${r.compania?.nombre || ""}</h3>
      <p style="text-align:center;margin:0">${r.compania?.direccion || ""}</p>
      <p style="text-align:center;margin:0 0 14px">${
        r.compania?.rnc ? "RNC: " + r.compania.rnc : ""
      }</p>
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">Recibo de Cobros Ventas Miscelaneas</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Fecha", r.fecha)}
        ${fila("Tipo Ingreso", r.tipogasto)}
        ${fila("Tipo Factura", r.tipofactura)}
        ${r.noprest ? fila("# Préstamo", r.noprest) : ""}
        ${fila("Cédula", r.cedula)}
        ${fila("Nombres", r.nombres)}
        ${r.gps ? fila("GPS", r.gps) : ""}
        ${r.vehiculo ? fila("Vehículo", r.vehiculo) : ""}
        ${fila("Valor", "RD$ " + fmtMoney(r.valor))}
        ${
          r.distribucion
            ? fila(
                "Forma de cobro",
                `Distribuido en ${r.distribucion.cuotas} cuotas del préstamo · cuota RD$ ${fmtMoney(
                  r.distribucion.valorcuota
                )}`
              )
            : ""
        }
        ${r.tipodepago ? fila("Tipo de pago", r.tipodepago) : ""}
        ${r.referenciapago ? fila("Referencia", r.referenciapago) : ""}
        ${r.descripcion ? fila("Descripción", r.descripcion) : ""}
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Preparado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function RegistrarVentaGeneral({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [config, setConfig] = useState(null);
  const [prestamos, setPrestamos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [tipogasto, setTipogasto] = useState("");
  const [tipofactura, setTipofactura] = useState("Contado");
  const [gps, setGps] = useState("");
  const [prestamo, setPrestamo] = useState(null);
  const [nombres, setNombres] = useState("");
  const [cedula, setCedula] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [valor, setValor] = useState("");
  const [tipodepago, setTipodepago] = useState("Contado");
  const [referenciapago, setReferenciapago] = useState("");
  // Distribuir en las cuotas: el valor se suma al capital de las cuotas
  // pendientes y a la deuda del préstamo (el backend hace todo al guardar).
  const [distribuir, setDistribuir] = useState(false);
  const [infoCuotas, setInfoCuotas] = useState(null); // null = sin consultar
  // En cuántas cuotas repartir (las más próximas). Por defecto, todas.
  const [cuotasDist, setCuotasDist] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargarConfig = async () => {
    setCargando(true);
    try {
      const [conf, prest] = await Promise.all([
        post("/api/ventageneral_config", {}),
        post("/api/ventageneral_prestamos", {}),
      ]);
      if (conf.ok) setConfig(conf.data);
      else
        showAlert({
          message: conf.data.Error || "No se pudo cargar la configuración",
          color: "error",
        });
      if (prest.ok) setPrestamos(prest.data.prestamos || []);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modoPrestamo = config?.asociarprestamo === "S";
  // La categoría elegida puede exigir el número de GPS (se marca en Categorías).
  const gpsObligatorio = (config?.categorias || []).some(
    (c) => c.categoria === tipogasto && c.gpsobligatorio === "S"
  );
  const esContadoDirecto = tipofactura === "Contado" && !prestamo;

  const limpiar = () => {
    setTipogasto("");
    setTipofactura("Contado");
    setGps("");
    setPrestamo(null);
    setNombres("");
    setCedula("");
    setDescripcion("");
    setValor("");
    setTipodepago("Contado");
    setReferenciapago("");
    setDistribuir(false);
    setInfoCuotas(null);
    setCuotasDist("");
  };

  const elegirPrestamo = async (p) => {
    setPrestamo(p);
    setInfoCuotas(null);
    setCuotasDist("");
    if (!p) {
      setDistribuir(false);
      return;
    }
    setNombres(p.nombres || "");
    setCedula(p.cedula || "");
    // Cuotas pendientes y cuota actual, para el preview del checkbox.
    const { ok, data } = await post("/api/ventageneral_cuotaspendientes", {
      noprest: p.noprest,
    });
    const info = ok ? data : { cuotas: 0, valorcuota: 0 };
    setInfoCuotas(info);
    // Por defecto se reparte entre todas las pendientes.
    setCuotasDist(toNum(info.cuotas) > 0 ? String(toNum(info.cuotas)) : "");
  };

  const esCreditoConPrestamo = tipofactura === "Credito" && !!prestamo;
  const distribuirActivo = esCreditoConPrestamo && distribuir;
  const nCuotas = toNum(infoCuotas?.cuotas);
  // Cuotas en las que se reparte: entre 1 y las pendientes (las más próximas).
  const cuotasUsadas = Math.max(1, Math.min(toNum(cuotasDist) || nCuotas, nCuotas));
  const porCuota = cuotasUsadas > 0 ? toNum(valor) / cuotasUsadas : 0;

  let chipCobro = "Venta a crédito — queda pendiente de cobro";
  if (distribuirActivo) chipCobro = "El valor se suma a las cuotas del préstamo";
  else if (prestamo) chipCobro = "La venta queda pendiente de cobro asociada al préstamo";

  let previewCuotas = `${nCuotas} cuotas pendientes`;
  if (infoCuotas === null) previewCuotas = "Consultando cuotas pendientes…";
  else if (nCuotas === 0) previewCuotas = "El préstamo no tiene cuotas pendientes";
  else if (distribuir && toNum(valor) > 0) {
    const alcance =
      cuotasUsadas < nCuotas
        ? `las próximas ${cuotasUsadas} de ${nCuotas} cuotas`
        : `las ${nCuotas} cuotas`;
    previewCuotas =
      `RD$ ${fmtMoney(porCuota)} más en ${alcance}` +
      (toNum(infoCuotas?.valorcuota) > 0
        ? ` · la cuota pasa de RD$ ${fmtMoney(infoCuotas.valorcuota)} a RD$ ${fmtMoney(
            toNum(infoCuotas.valorcuota) + porCuota
          )}`
        : "");
  }

  const guardar = async () => {
    if (!nombres.trim()) {
      showAlert({ message: "Los nombres del cliente no pueden estar en blanco", color: "warning" });
      return;
    }
    if (toNum(valor) <= 0) {
      showAlert({ message: "El valor no puede ser 0", color: "warning" });
      return;
    }
    if (!tipogasto) {
      showAlert({ message: "Debe indicar el tipo de ingreso", color: "warning" });
      return;
    }
    if (gpsObligatorio && !gps.trim()) {
      showAlert({
        message: `La categoría ${tipogasto} requiere el número de GPS`,
        color: "warning",
      });
      return;
    }
    if (modoPrestamo && tipofactura === "Credito" && !prestamo) {
      showAlert({
        message: "Debe asignar un préstamo cuando la factura es a crédito",
        color: "warning",
      });
      return;
    }
    if (distribuirActivo && nCuotas === 0) {
      showAlert({
        message: "El préstamo no tiene cuotas pendientes para distribuir la venta",
        color: "warning",
      });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/ventageneral_guardar", {
        nombres: nombres.trim(),
        cedula: cedula.trim(),
        gps: gps.trim(),
        descripcion: descripcion.trim(),
        tipogasto,
        tipofactura,
        noprest: prestamo?.noprest || 0,
        valor: toNum(valor),
        tipodepago: esContadoDirecto ? tipodepago : "",
        referenciapago: esContadoDirecto ? referenciapago.trim() : "",
        distribuir: distribuirActivo,
        ncuotas: distribuirActivo ? cuotasUsadas : 0,
      });
      if (ok) {
        showAlert({ message: data.ok || "Venta registrada", color: "success" });
        imprimirReciboVentaGeneral({
          compania: data.compania,
          fecha: data.fecha,
          tipogasto,
          tipofactura,
          noprest: prestamo?.noprest || "",
          cedula,
          nombres,
          gps,
          vehiculo: prestamo
            ? [prestamo.marca, prestamo.modelo, prestamo.colore].filter(Boolean).join(" · ")
            : "",
          valor: toNum(valor),
          tipodepago: esContadoDirecto ? tipodepago : "",
          referenciapago: esContadoDirecto ? referenciapago : "",
          descripcion,
          distribucion: data.distribucion || null,
        });
        limpiar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar la venta", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  // ---- Mantenimiento de categorías (solo administradores) ----
  const [catOpen, setCatOpen] = useState(false);
  const [nuevaCat, setNuevaCat] = useState("");
  const [catBusy, setCatBusy] = useState(false);

  const agregarCategoria = async () => {
    if (!nuevaCat.trim()) return;
    setCatBusy(true);
    try {
      const { ok, data } = await post("/api/ventageneral_categoria_guardar", {
        categoria: nuevaCat.trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || "Categoría registrada", color: "success" });
        setNuevaCat("");
        const conf = await post("/api/ventageneral_config", {});
        if (conf.ok) setConfig(conf.data);
      } else {
        showAlert({ message: data.Error || "No se pudo registrar la categoría", color: "error" });
      }
    } finally {
      setCatBusy(false);
    }
  };

  const cambiarGpsObligatorio = async (c, marcado) => {
    setCatBusy(true);
    try {
      const { ok, data } = await post("/api/ventageneral_categoria_gpsobligatorio", {
        id: c.id,
        gpsobligatorio: marcado ? "S" : "N",
      });
      if (ok) {
        setConfig((prev) => ({
          ...prev,
          categorias: (prev?.categorias || []).map((x) =>
            x.id === c.id ? { ...x, gpsobligatorio: marcado ? "S" : "N" } : x
          ),
        }));
      } else {
        showAlert({ message: data.Error || "No se pudo actualizar la categoría", color: "error" });
      }
    } finally {
      setCatBusy(false);
    }
  };

  const eliminarCategoria = async (c) => {
    setCatBusy(true);
    try {
      const { ok, data } = await post("/api/ventageneral_categoria_eliminar", { id: c.id });
      if (ok) {
        showAlert({ message: data.ok || "Categoría eliminada", color: "success" });
        const conf = await post("/api/ventageneral_config", {});
        if (conf.ok) setConfig(conf.data);
      } else {
        showAlert({ message: data.Error || "No se pudo eliminar la categoría", color: "error" });
      }
    } finally {
      setCatBusy(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registrar Ventas Generales"
        subtitle="Ventas misceláneas (GPS, seguros, servicios) al contado o a crédito"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Venta */}
        <Box sx={sectionBox}>
          <SectionTitle
            icon={SellOutlinedIcon}
            color={FP.color.accentBlue}
            extra={
              config?.esadmin && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCatOpen(true)}
                  sx={{ ...btnGhost, ml: "auto" }}
                >
                  Categorías
                </Button>
              )
            }
          >
            Datos de la Venta
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo Ingreso"
                value={tipogasto}
                onChange={(e) => setTipogasto(e.target.value)}
                disabled={cargando}
                sx={fpInputSx}
              >
                {(config?.categorias || []).map((c) => (
                  <MenuItem key={c.id} value={c.categoria}>
                    {c.categoria}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo Factura"
                value={tipofactura}
                onChange={(e) => setTipofactura(e.target.value)}
                sx={fpInputSx}
              >
                {(config?.tiposFactura || ["Contado", "Credito"]).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="GPS Número"
                required={gpsObligatorio}
                value={gps}
                onChange={(e) => setGps(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Valor"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="0.00"
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            {modoPrestamo && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  autoHighlight
                  selectOnFocus
                  handleHomeEndKeys
                  filterOptions={filtrarPrestamos}
                  options={prestamos}
                  loading={cargando}
                  value={prestamo}
                  getOptionLabel={(o) =>
                    typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                  }
                  isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                  onChange={(_, v) => elegirPrestamo(v)}
                  renderOption={(props, o) => (
                    <li {...props} key={o.noprest}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          {o.noprest} — {o.nombres}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                          {[o.cedula, o.chasis, [o.marca, o.modelo].filter(Boolean).join(" ")]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Asociar a Préstamo"
                      helperText={
                        tipofactura === "Credito"
                          ? "Obligatorio para ventas a crédito"
                          : "Opcional — la venta queda pendiente de cobro"
                      }
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
            )}
            {prestamo && (
              <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={[prestamo.marca, prestamo.modelo, prestamo.colore]
                      .filter(Boolean)
                      .join(" · ")}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Chasis ${prestamo.chasis || "—"}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Cliente */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.success}>
            Cliente
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Nombres"
                value={nombres}
                onChange={(e) => setNombres(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Cédula"
                value={cedula}
                onChange={(e) => setCedula(formatCedula(e.target.value))}
                placeholder="000-0000000-0"
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Descripción Ingreso"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Pago (solo contado directo) */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.navy}>
            Cobro
          </SectionTitle>
          <Grid container spacing={2}>
            {esContadoDirecto ? (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de pago"
                    value={tipodepago}
                    onChange={(e) => setTipodepago(e.target.value)}
                    sx={fpInputSx}
                  >
                    {(config?.tiposPagoVenta || ["Contado", "Transferencias"]).map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    label="Referencia / descripción del pago"
                    value={referenciapago}
                    onChange={(e) => setReferenciapago(e.target.value)}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={8}>
                <Chip
                  label={chipCobro}
                  sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                />
                {esCreditoConPrestamo && (
                  <Box
                    sx={{
                      mt: 1.4,
                      p: 1.2,
                      borderRadius: 2,
                      border: `1px solid ${distribuir ? FP.color.accentBlue : FP.color.border}`,
                      bgcolor: distribuir ? `${FP.color.accentBlue}0c` : "#FFFFFF",
                    }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, alignItems: "flex-start" }}
                      control={
                        <Checkbox
                          size="small"
                          checked={distribuir}
                          onChange={(e) => setDistribuir(e.target.checked)}
                          disabled={nCuotas === 0}
                          sx={{ p: 0.2, mt: 0.1 }}
                        />
                      }
                      label={
                        <Box sx={{ ml: 0.8 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                            Distribuir el valor entre las cuotas del préstamo
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.75rem", color: FP.color.textMuted, mt: 0.2 }}
                          >
                            {previewCuotas}
                          </Typography>
                        </Box>
                      }
                    />
                    {distribuir && nCuotas > 0 && (
                      <TextField
                        type="number"
                        size="small"
                        label="En cuántas cuotas"
                        value={cuotasDist}
                        onChange={(e) => setCuotasDist(e.target.value)}
                        onBlur={() => setCuotasDist(String(cuotasUsadas))}
                        inputProps={{ min: 1, max: nCuotas }}
                        helperText={`Máximo ${nCuotas} (las más próximas)`}
                        sx={{ ...fpInputSx, mt: 1.2, ml: 4, width: 190 }}
                      />
                    )}
                  </Box>
                )}
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={guardar}
                disabled={guardando || cargando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Guardar e imprimir
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Categorías */}
      <Dialog
        open={catOpen}
        onClose={() => setCatOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Categorías de Ventas Generales</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Nueva categoría"
              value={nuevaCat}
              onChange={(e) => setNuevaCat(e.target.value)}
              sx={fpInputSx}
            />
            <Button
              startIcon={catBusy ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              onClick={agregarCategoria}
              disabled={catBusy || !nuevaCat.trim()}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Agregar
            </Button>
          </Stack>
          <Stack spacing={0.6}>
            {(config?.categorias || []).map((c) => (
              <Stack
                key={c.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.4,
                  py: 0.6,
                  borderRadius: 1.6,
                  border: `1px solid ${FP.color.border}`,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", flex: 1 }}>
                  {c.categoria}
                </Typography>
                <FormControlLabel
                  sx={{ mr: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={c.gpsobligatorio === "S"}
                      onChange={(e) => cambiarGpsObligatorio(c, e.target.checked)}
                      disabled={catBusy}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.78rem" }}>GPS obligatorio</Typography>}
                />
                <IconButton
                  size="small"
                  onClick={() => eliminarCategoria(c)}
                  disabled={catBusy || c.categoria === "Gps"}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCatOpen(false)} sx={btnGhost}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
